<template>
    <widget-wrapper :loader="api" :title="$t('widgets.tracker_graph.title')" id="tracker-graph">
        <graph-container :datasets="datasets" :responsive="true" :dropdown-limit="Infinity">
            <template slot-scope="scope">
                <tracker-graph :chart-data="{datasets: scope.datasets, labels}" :height="200" :step="graphStep"
                               :value="highlight" @input="onHighlightClick"
                               :label-xaxis="axisLabels.xAxis" :label-yaxis="axisLabels.yAxis"/>
            </template>
        </graph-container>
    </widget-wrapper>
</template>

<script>
    import {Component} from 'vue-property-decorator';
    import DashboardWidget from '../DashboardWidget';
    import WidgetWrapper from '../WidgetWrapper';
    import WidgetData from '../WidgetDataDecorator';
    import TrackerGraph from './TrackerGraph';
    import moment from 'moment';
    import _ from 'lodash';
    import GraphContainer from '../../graphs/GraphContainer';
    import {calculateDaterangeStep} from '../../../api/format-query';

    @Component({
        components: {
            GraphContainer,
            WidgetWrapper,
            TrackerGraph,
        }
    })
    export default class TrackerGraphWidget extends DashboardWidget {
        api = {
            endpoint: 'dashboard/api/sales/contact_moments',
        };
        @WidgetData(() => [])
        labels;
        @WidgetData(() => [])
        datasets;
        settingHighlight = false;
        @WidgetData(() => [])
        legendLabels;

        get highlight() {
            if (this.query.daterange.highlight) {
                return this.query.daterange.highlight.toDate();
            } else {
                return null;
            }
        }

        set highlight(value) {
            this.settingHighlight = true; // Signal we are changing the highlight
            if (value) {
                this.settingHighlight = true;
                this.query.daterange.highlight = moment(value);
            } else {
                this.query.daterange.highlight = null;
            }
        }

        onHighlightClick(newValue) {
            if (this.highlight) {
                this.highlight = null;
            } else {
                if (this.graphStep === 'month') {
                    // If we are showing a graph with steps of a month, then change the daterange to that month.
                    this.highlightMonth(newValue);
                } else {
                    // Otherwise the graph is showing data per-day, so highlight that day.
                    this.highlight = newValue;
                }
            }
        }

        highlightMonth(firstDay) {
            const start = moment(firstDay).startOf('month');
            const end = moment(start).endOf('month');
            this.query.daterange.start = start;
            this.query.daterange.end = end;
        }

        shouldRequestData() {
            // If the highlight is being set/unset then we should not re-request the data of the graph.
            // We only surpress it if this component has been changing the highlight.
            // If the daterange or highlight changes outside of us then we still re-request the data.
            const result = this.settingHighlight;
            this.settingHighlight = false;
            return !result;
        }

        onQueryData(response) {
            if (!response) {
                this.labels = [];
                this.datasets = [];
                return;
            }
            // Get the labels for the datasets that were previously enabled through the dropdown
            const enabledLabels = this.datasets.filter(dataset => dataset.enabled).map(dataset => dataset.label);

            const graphLegend = response.data.legend;
            const graphData = response.data.data;
            this.labels = _.keys(graphData).map(label => new Date(label));
            this.datasets = graphLegend.map((name, index) => ({
                label: graphLegend[index],
                data: _.map(graphData, column => column[index]),
                hidden: false,
                enabled: enabledLabels.includes(graphLegend[index]), // If the dataset was selected with the dropdown in the GraphLegend component, then we enable it here so the selection is persistent
            }));
        }

        get graphStep() {
            return calculateDaterangeStep(this.query.daterange.start, this.query.daterange.end);
        }

        get axisLabels() {
            return {
                xAxis: this.$t('widgets.tracker_graph.label_xaxis'),
                yAxis: this.$t('widgets.tracker_graph.label_yaxis'),
            };
        }
    }
</script>



// WEBPACK FOOTER //
// src/components/widgets/tracker-graph/TrackerGraphWidget.vue